import type { CSSProperties, ReactNode } from 'react';
import { SidebarInset, SidebarProvider } from '@clay-run/glaze/sidebar-next/index';
import { useMiniAppDarkMode } from '../../../../../../mini-apps/_shared/components/MiniAppLayout';
import NavRail from './NavRail';
import type { RailOverrides } from './rail/rail-items';
import { V2_ZOOM } from './zoom';

// The rail's width, set as the custom property Glaze's icon-mode sidebar reads.
// The rail is always the icon strip, so Glaze's own `--sidebar-width` default
// never applies to it.
const RAIL_WIDTHS: CSSProperties & Record<`--${string}`, string> = {
  '--sidebar-width-icon': '3.5rem',
};

/**
 * The V2 shell's frame: the viewport frame, the zoom root, the rail's
 * `SidebarProvider`, and `NavRail`. The provider is pinned closed and does not
 * persist, so the rail is always the icon strip. `children` fill the row
 * beside the rail (below 768px the rail is the floating hamburger and the
 * content is inset below it). `ShellLayout` puts the routed page there, so every
 * framed page shares one rail.
 */
export function V2Shell({ children, railOverrides }: { children: ReactNode; railOverrides?: RailOverrides }) {
  useMiniAppDarkMode();

  return (
    // The outer root stays unzoomed so viewport sizing (`h-dvh`) is computed
    // in real px. The inner wrapper carries the shell zoom (`V2_ZOOM`, see
    // `zoom.ts`). Percentage lengths are not scaled by `zoom`, so the inner
    // wrapper still fills the outer root.
    <div className="flex h-dvh overflow-hidden">
      <div data-v2-zoom-root className="flex h-full w-full" style={{ zoom: V2_ZOOM }}>
        <SidebarProvider open={false} persistOpenState={false} className="h-full" style={RAIL_WIDTHS}>
          <NavRail overrides={railOverrides} />
          <SidebarInset className="overflow-hidden">
            {/* pt-12 md:pt-0: below 768px NavRail is a fixed hamburger
                (`top-3 left-3`) that reserves no layout width, so the content
                column starts below it. The one place this inset lives; pages
                and MiniAppLayout (while framed) do not add their own. */}
            <div className="flex h-full min-w-0 flex-row overflow-hidden pt-12 md:pt-0">{children}</div>
          </SidebarInset>
        </SidebarProvider>
      </div>
    </div>
  );
}
